import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client.js';
import { formatBrl } from '../../utils/currency.js';
import { SUBSCRIPTION_BILLING_PAGE_PATH } from '../../utils/subscriptionBilling.js';
import { orderStatusLabelPtBr } from '../../utils/orderStatus.js';

/** @param {unknown} n */
function formatInt(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('pt-BR').format(Math.round(v));
}

function localYmd(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** @param {string} ymd @param {number} deltaDays */
function shiftYmd(ymd, deltaDays) {
  const [y, mo, dd] = ymd.split('-').map(Number);
  const dt = new Date(y, mo - 1, dd + deltaDays);
  return localYmd(dt);
}

const STATUS_ORDER = ['pending', 'paid', 'sent_to_erp', 'cancelled'];
const STATUS_COLORS = {
  pending: '#f59e0b',
  paid: '#059669',
  sent_to_erp: '#0ea5e9',
  cancelled: '#71717a',
};

/** @param {{ counts: Record<string, number> }} props */
function OrdersStatusDonut({ counts }) {
  const total = STATUS_ORDER.reduce((s, k) => s + (counts[k] || 0), 0);
  if (total === 0) {
    return (
      <div className="flex h-36 items-center justify-center rounded-2xl border border-dashed border-ink/15 text-center text-sm text-ink/50 dark:border-white/10 dark:text-frost-muted">
        Sem pedidos no período seleccionado
      </div>
    );
  }
  let acc = 0;
  const segments = STATUS_ORDER.map((k) => {
    const n = counts[k] || 0;
    const start = (acc / total) * 360;
    acc += n;
    const end = (acc / total) * 360;
    return { k, start, end, color: STATUS_COLORS[k] };
  }).filter((s) => s.end > s.start);

  let gradient = '';
  segments.forEach((s) => {
    gradient += `${s.color} ${s.start}deg ${s.end}deg, `;
  });
  gradient = gradient.replace(/, $/, '');

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between sm:gap-6">
      <div
        className="relative h-32 w-32 shrink-0 rounded-full p-2.5 shadow-lg shadow-black/10 dark:shadow-black/40"
        style={{ background: `conic-gradient(${gradient})` }}
      >
        <div className="flex h-full w-full items-center justify-center rounded-full bg-white dark:bg-ink">
          <div className="text-center">
            <p className="font-display text-xl font-bold text-ink tabular-nums dark:text-mist">{formatInt(total)}</p>
            <p className="text-[9px] font-bold uppercase tracking-wider text-ink/50 dark:text-frost-muted">pedidos</p>
          </div>
        </div>
      </div>
      <ul className="grid w-full gap-2 text-xs sm:max-w-[12rem]">
        {STATUS_ORDER.map((k) => (
          <li key={k} className="flex items-center justify-between gap-2 rounded-lg bg-black/[0.03] px-2 py-1.5 dark:bg-white/[0.04]">
            <span className="flex items-center gap-2 min-w-0">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: STATUS_COLORS[k] }} />
              <span className="truncate font-medium text-ink/80 dark:text-mist/85">{orderStatusLabelPtBr(k)}</span>
            </span>
            <span className="tabular-nums font-semibold text-ink/60 dark:text-frost">{formatInt(counts[k] || 0)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** @param {{ days: string[], byDay: Record<string, { revenue: number, cnt: number }> }} props */
function RevenueSparkBars({ days, byDay }) {
  const maxRev = Math.max(1, ...days.map((d) => Number(byDay[d]?.revenue || 0)));
  return (
    <div className="flex h-40 items-end gap-1 px-0.5">
      {days.map((d) => {
        const cell = byDay[d] || { revenue: 0, cnt: 0 };
        const hPct = Math.round((Number(cell.revenue) / maxRev) * 100);
        const short = `${d.slice(8, 10)}/${d.slice(5, 7)}`;
        return (
          <div
            key={d}
            title={`${d}: ${formatBrl(cell.revenue)} · ${formatInt(cell.cnt)} pedidos`}
            className="flex min-w-0 flex-1 flex-col items-center gap-1"
          >
            <div className="flex w-full flex-1 items-end justify-center">
              <div
                className="w-full max-w-[10px] rounded-t-md bg-gradient-to-t from-accent/95 to-emerald-400/80 opacity-95 dark:from-accent dark:to-emerald-300/70"
                style={{ height: `${Math.max(8, hPct)}%` }}
              />
            </div>
            <span className="tabular-nums text-[8px] font-bold uppercase text-ink/50 dark:text-frost-muted">{short}</span>
          </div>
        );
      })}
    </div>
  );
}

/** @param {{ label: string, value: string, hint?: string, onClick?: () => void }} props */
function KpiCard({ label, value, hint, onClick }) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl border border-ink/10 bg-white/90 p-4 text-left shadow-sm backdrop-blur-sm transition dark:border-white/10 dark:bg-white/[0.06] ${
        onClick
          ? 'cursor-pointer hover:border-accent/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 dark:hover:border-accent/35'
          : ''
      }`}
    >
      <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br from-accent/25 to-emerald-400/10 blur-2xl dark:from-accent/20" />
      <p className="relative text-[10px] font-bold uppercase tracking-[0.2em] text-ink/55 dark:text-frost-muted">{label}</p>
      <p className="relative mt-2 font-display text-2xl font-semibold tabular-nums tracking-tight text-ink dark:text-mist">
        {value}
      </p>
      {hint ? <p className="relative mt-1.5 text-[11px] leading-snug text-ink/55 dark:text-frost-muted">{hint}</p> : null}
    </Comp>
  );
}

/**
 * Painel inicial tipo BI — agrega `/admin/tenants`, `/admin/users` e `/admin/finance/summary`.
 * @param {{ onOpenTab: (id: string) => void }} props
 */
export function AdminOverviewSection({ onOpenTab }) {
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');
  /** @type {any[]} */
  const [tenants, setTenants] = useState([]);
  /** @type {any[]} */
  const [users, setUsers] = useState([]);
  /** @type {Record<string, unknown> | null} */
  const [summary, setSummary] = useState(null);
  /** @type {'7' | '30'} */
  const [finDays, setFinDays] = useState('30');

  const summaryQs = useMemo(() => {
    const to = localYmd();
    const from = shiftYmd(to, finDays === '7' ? -6 : -29);
    return `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  }, [finDays]);

  const load = useCallback(async () => {
    setBusy(true);
    setErr('');
    try {
      const [tRes, uRes, sRes] = await Promise.all([
        api('/admin/tenants'),
        api('/admin/users'),
        api(`/admin/finance/summary${summaryQs}`),
      ]);
      setTenants(Array.isArray(tRes.tenants) ? tRes.tenants : []);
      setUsers(Array.isArray(uRes.users) ? uRes.users : []);
      setSummary(sRes && typeof sRes === 'object' ? sRes : null);
    } catch (e) {
      setTenants([]);
      setUsers([]);
      setSummary(null);
      setErr(e.message || 'Erro ao carregar visão geral.');
    } finally {
      setBusy(false);
    }
  }, [summaryQs]);

  useEffect(() => {
    load();
  }, [load]);

  const metrics = useMemo(() => {
    const platformUsers = users.filter((u) => u.role !== 'super_admin');
    const gold = platformUsers.filter((u) => u.billing_plan === 'gold').length;
    const essential = platformUsers.filter((u) => u.billing_plan !== 'gold').length;
    const blockedU = users.filter((u) => u.blocked).length;

    const now = Date.now();
    const trialEnding = platformUsers.filter((u) => {
      if (u.billing_plan === 'gold' || u.blocked) return false;
      const raw = u.trial_ends_at;
      if (!raw) return false;
      const t = new Date(String(raw).replace(' ', 'T'));
      if (Number.isNaN(t.getTime())) return false;
      const days = Math.ceil((t.getTime() - now) / 86400000);
      return days >= 0 && days <= 7;
    }).length;

    const activeT = tenants.filter((t) => t.status !== 'blocked').length;
    const blockedT = tenants.filter((t) => t.status === 'blocked').length;
    const billingOverdue = tenants.filter((t) => Number(t.billing_overdue_blocked) === 1).length;
    const subPendingLines = tenants.filter((t) => Number(t.sub_pending_cnt) > 0).length;
    const subPendingSum = tenants.reduce((s, t) => s + Number(t.sub_pending_sum_brl || 0), 0);

    const payOnline = tenants.filter((t) => Number(t.payment_online_enabled) === 1).length;
    const mpOk = tenants.filter((t) => Number(t.mp_token_configured) === 1).length;

    const g = summary?.global && typeof summary.global === 'object' ? summary.global : {};
    const ordersTotal = Number(g.orders_total || 0);
    const gmv = Number(g.gmv_all || 0);
    const revenueOk = Number(g.revenue_confirmed || 0);
    const pipeline = Number(g.pipeline_pending || 0);
    const discountEst = Number(g.discount_value_estimated || 0);
    const withCoupon = Number(g.orders_with_coupon || 0);

    return {
      platformUsers: platformUsers.length,
      gold,
      essential,
      blockedU,
      trialEnding,
      activeT,
      blockedT,
      billingOverdue,
      subPendingLines,
      subPendingSum,
      payOnline,
      mpOk,
      ordersTotal,
      gmv,
      revenueOk,
      pipeline,
      discountEst,
      withCoupon,
    };
  }, [tenants, users, summary]);

  const donutCounts = useMemo(() => {
    const g = summary?.global && typeof summary.global === 'object' ? summary.global : {};
    const confirmed = Number(g.count_confirmed || 0);
    const sentErp = Number(g.count_sent_to_erp || 0);
    return {
      pending: Number(g.count_pending || 0),
      paid: Math.max(0, confirmed - sentErp),
      sent_to_erp: sentErp,
      cancelled: Number(g.count_cancelled || 0),
    };
  }, [summary]);

  const { chartDays, byDayMap } = useMemo(() => {
    const rows = Array.isArray(summary?.by_day) ? summary.by_day : [];
    const map = {};
    for (const row of rows) {
      const d = row?.d;
      if (typeof d !== 'string' || d.length < 10) continue;
      const key = d.slice(0, 10);
      map[key] = {
        revenue: Number(row.revenue || 0),
        cnt: Number(row.cnt || 0),
      };
    }
    const keys = Object.keys(map).sort();
    const last = keys.slice(-14);
    return { chartDays: last, byDayMap: map };
  }, [summary]);

  const topTenants = useMemo(() => {
    const rows = Array.isArray(summary?.by_tenant) ? summary.by_tenant : [];
    return rows.slice(0, 8);
  }, [summary]);

  const alerts = useMemo(() => {
    const list = [];
    if (metrics.billingOverdue > 0) {
      list.push({
        key: 'bill',
        tone: 'amber',
        text: `${metrics.billingOverdue} empresa(s) bloqueada(s) por mensalidade em atraso. Regularize pelo portal ${SUBSCRIPTION_BILLING_PAGE_PATH}.`,
        action: () => onOpenTab('finance'),
        href: SUBSCRIPTION_BILLING_PAGE_PATH,
      });
    }
    if (metrics.subPendingLines > 0) {
      list.push({
        key: 'sub',
        tone: 'violet',
        text: `${metrics.subPendingLines} empresa(s) com cobrança SaaS pendente (${formatBrl(metrics.subPendingSum)}). Pagamento no portal, sem depender do e-mail do dono.`,
        action: () => onOpenTab('finance'),
        href: SUBSCRIPTION_BILLING_PAGE_PATH,
      });
    }
    if (metrics.blockedU > 0) {
      list.push({
        key: 'usr',
        tone: 'rose',
        text: `${metrics.blockedU} utilizador(es) de plataforma bloqueado(s).`,
        action: () => onOpenTab('users'),
      });
    }
    if (metrics.trialEnding > 0) {
      list.push({
        key: 'trial',
        tone: 'sky',
        text: `${metrics.trialEnding} conta(s) em trial Essential com fim nos próximos 7 dias.`,
        action: () => onOpenTab('users'),
      });
    }
    return list;
  }, [metrics, onOpenTab]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h3 className="font-display text-xl font-semibold text-ink dark:text-mist">Visão geral</h3>
          <p className="mt-1 max-w-2xl text-sm text-ink/65 dark:text-frost-muted">
            Indicadores da plataforma em tempo quase real — empresas, utilizadores e movimento de pedidos. Use os
            atalhos para aprofundar sem perder contexto.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-ink/55 dark:text-frost-muted">
            Pedidos (GMV)
          </span>
          <div className="inline-flex rounded-2xl border border-ink/10 bg-black/[0.04] p-1 dark:border-white/12 dark:bg-white/[0.1]">
            {[
              ['7', '7 dias'],
              ['30', '30 dias'],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setFinDays(id)}
                className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition ${
                  finDays === id
                    ? 'bg-white text-ink shadow dark:bg-ink dark:text-mist'
                    : 'text-ink/65 dark:bg-white/[0.08] dark:text-frost dark:hover:bg-white/[0.12]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => load()}
            disabled={busy}
            className="rounded-2xl border border-ink/15 px-4 py-2 text-xs font-semibold text-ink disabled:opacity-50 dark:border-white/20 dark:bg-white/[0.06] dark:text-frost dark:hover:bg-white/[0.1]"
          >
            {busy ? 'A actualizar…' : 'Actualizar'}
          </button>
        </div>
      </div>

      {err ? (
        <div className="rounded-2xl border border-accent2/40 bg-accent2/15 px-4 py-3 text-sm font-semibold text-accent2">
          {err}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {[
          ['finance', 'Financeiro detalhado'],
          ['plans', 'Planos SaaS'],
          ['tenants', 'Empresas'],
          ['users', 'Utilizadores'],
          ['templates', 'Templates'],
          ['logs', 'Logs de auditoria'],
          ['erpLogs', 'Integração ERP'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => onOpenTab(id)}
            className="rounded-full border border-ink/15 bg-white/80 px-3 py-1.5 text-[11px] font-semibold text-ink/80 transition hover:border-accent/40 hover:bg-accent/5 dark:border-white/15 dark:bg-white/[0.05] dark:text-mist/85 dark:hover:border-accent/35"
          >
            {label}
          </button>
        ))}
      </div>

      {alerts.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {alerts.map((a) => (
            <div
              key={a.key}
              className={`rounded-2xl border px-4 py-3 text-left text-xs font-medium leading-snug ${
                a.tone === 'amber'
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100'
                  : a.tone === 'violet'
                    ? 'border-violet-500/40 bg-violet-500/10 text-violet-950 dark:text-violet-100'
                    : a.tone === 'rose'
                      ? 'border-rose-500/40 bg-rose-500/10 text-rose-950 dark:text-rose-100'
                      : 'border-sky-500/40 bg-sky-500/10 text-sky-950 dark:text-sky-100'
              }`}
            >
              <span className="font-semibold">Atenção · </span>
              {a.text}
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={a.action}
                  className="rounded-lg border border-current/20 bg-white/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide dark:bg-white/10"
                >
                  Financeiro admin
                </button>
                {a.href ? (
                  <Link
                    to={a.href}
                    className="rounded-lg border border-current/20 bg-white/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide dark:bg-white/10"
                  >
                    Portal mensalidade
                  </Link>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Empresas activas"
          value={busy ? '…' : formatInt(metrics.activeT)}
          hint={metrics.blockedT ? `${formatInt(metrics.blockedT)} bloqueada(s) manualmente` : 'Todas desbloqueadas'}
          onClick={() => onOpenTab('tenants')}
        />
        <KpiCard
          label="Utilizadores (plataforma)"
          value={busy ? '…' : formatInt(metrics.platformUsers)}
          hint={`Ouro ${formatInt(metrics.gold)} · Essential/trial ${formatInt(metrics.essential)}`}
          onClick={() => onOpenTab('users')}
        />
        <KpiCard
          label="Pedidos no período"
          value={busy ? '…' : formatInt(metrics.ordersTotal)}
          hint={`GMV ${formatBrl(metrics.gmv)} · Confirmado ${formatBrl(metrics.revenueOk)}`}
          onClick={() => onOpenTab('finance')}
        />
        <KpiCard
          label="Pagamento online"
          value={
            busy
              ? '…'
              : tenants.length === 0
                ? '—'
                : `${formatInt(metrics.payOnline)}/${formatInt(tenants.length)}`
          }
          hint={`Mercado Pago token: ${formatInt(metrics.mpOk)} loja(s)`}
          onClick={() => onOpenTab('tenants')}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-ink/10 bg-white/85 p-5 dark:border-white/10 dark:bg-white/[0.05]">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="text-sm font-semibold text-ink dark:text-mist">Receita confirmada por dia</h4>
              <p className="mt-0.5 text-[11px] text-ink/55 dark:text-frost-muted">
                Soma dos pedidos pagos ou enviados ao ERP (últimos {chartDays.length || '…'} dias com movimento).
              </p>
            </div>
          </div>
          <div className="mt-4">
            {chartDays.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink/55 dark:text-frost-muted">Sem série diária neste intervalo.</p>
            ) : (
              <RevenueSparkBars days={chartDays} byDay={byDayMap} />
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 border-t border-ink/10 pt-4 text-[11px] dark:border-white/10 sm:grid-cols-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-frost">
                Pipeline pendente
              </p>
              <p className="mt-0.5 font-display text-lg font-semibold tabular-nums text-ink dark:text-mist">
                {formatBrl(metrics.pipeline)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-frost">
                Descontos (est.)
              </p>
              <p className="mt-0.5 font-display text-lg font-semibold tabular-nums text-ink dark:text-mist">
                {formatBrl(metrics.discountEst)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-frost">
                Com cupom
              </p>
              <p className="mt-0.5 font-display text-lg font-semibold tabular-nums text-ink dark:text-mist">
                {formatInt(metrics.withCoupon)}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-frost">
                Lojas no ranking
              </p>
              <p className="mt-0.5 font-display text-lg font-semibold tabular-nums text-ink dark:text-mist">
                {formatInt(topTenants.length)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-ink/10 bg-white/85 p-5 dark:border-white/10 dark:bg-white/[0.05]">
          <h4 className="text-sm font-semibold text-ink dark:text-mist">Estado dos pedidos</h4>
          <p className="mt-0.5 text-[11px] text-ink/55 dark:text-frost-muted">Distribuição no período seleccionado acima.</p>
          <div className="mt-5">
            <OrdersStatusDonut counts={donutCounts} />
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-ink/10 bg-white/85 p-5 dark:border-white/10 dark:bg-white/[0.05]">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-ink dark:text-mist">Top lojas por receita confirmada</h4>
            <p className="mt-0.5 text-[11px] text-ink/55 dark:text-frost-muted">No mesmo período (máx. 8 linhas).</p>
          </div>
          <button
            type="button"
            onClick={() => onOpenTab('finance')}
            className="text-[11px] font-semibold text-accent underline-offset-2 hover:underline"
          >
            Abrir painel financeiro →
          </button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-xs">
            <thead>
              <tr className="border-b border-ink/10 bg-zinc-100/90 text-[10px] font-bold uppercase tracking-wider text-ink dark:border-white/10 dark:bg-zinc-900/90 dark:text-mist">
                <th className="pb-2 pr-3">Loja</th>
                <th className="pb-2 pr-3">Slug</th>
                <th className="pb-2 pr-3 text-right">Pedidos</th>
                <th className="pb-2 pr-3 text-right">GMV</th>
                <th className="pb-2 text-right">Receita ok</th>
              </tr>
            </thead>
            <tbody>
              {topTenants.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-ink/55 dark:text-frost-muted">
                    {busy ? 'A carregar…' : 'Sem dados de pedidos neste período.'}
                  </td>
                </tr>
              ) : (
                topTenants.map((row) => (
                  <tr
                    key={String(row.tenant_id)}
                    className="border-b border-ink/5 last:border-0 dark:border-white/[0.06]"
                  >
                    <td className="py-2.5 pr-3 font-medium text-ink dark:text-mist">{String(row.tenant_nome ?? '—')}</td>
                    <td className="py-2.5 pr-3 font-mono text-[11px] text-ink/55 dark:text-frost-muted">
                      /{String(row.slug ?? '')}
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-ink/80 dark:text-mist/80">
                      {formatInt(row.orders_cnt)}
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-ink/80 dark:text-mist/80">
                      {formatBrl(row.gmv)}
                    </td>
                    <td className="py-2.5 text-right font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                      {formatBrl(row.revenue_ok)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
